import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, IconButton, Tooltip, Collapse, Menu, MenuItem, Checkbox, Button, CircularProgress, Snackbar, Alert, Dialog, DialogContent, InputBase } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon   from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon     from "@mui/icons-material/KeyboardArrowUp";
import LinkIcon                from "@mui/icons-material/Link";
import MoreVertIcon            from "@mui/icons-material/MoreVert";
import { useTheme } from "@mui/material/styles";
import LinkOffIcon             from "@mui/icons-material/LinkOff";
import CampaignOutlinedIcon    from "@mui/icons-material/CampaignOutlined";
import GroupAddIcon            from "@mui/icons-material/GroupAdd";
import ArticleIcon             from "@mui/icons-material/Article";
import InsertDriveFileIcon     from "@mui/icons-material/InsertDriveFile";
import MenuBookIcon            from "@mui/icons-material/MenuBook";
import ApartmentIcon           from "@mui/icons-material/Apartment";
import AnnounceContentDialog  from "./AnnounceContentDialog";
import AttachFormDialog       from "./AttachFormDialog";
import FormSubmissionsDialog  from "./FormSubmissionsDialog";
import { useSelector } from "react-redux";
import { ReduxRootStateType } from "../redux/store";
import { useGetUsersByCompany } from "../api/reactQueryHook/usePeople";
import { useGetLibrariesByCompany, useGetDepartmentsByLibrary, useGetFilesByDepartment } from "../api/reactQueryHook/useLibrary";
import { useGetFormsByCompany } from "../api/reactQueryHook/useForms";
import { useGetConnectedFormsByCompany, useConnectForm, useDisconnectForm } from "../api/reactQueryHook/useConnectedForms";
import { ConnectedForm_Type } from "../api/axiosNetworkRequest/connectedForms";
import { useAssignForm, useGetFormAssignments, useUnassignForm } from "../api/reactQueryHook/useFormAssignments";
import { useAnnounceContent } from "../api/reactQueryHook/useAnnounce";
import { getUsersByRole } from "../api/axiosNetworkRequest/roles";
import { Department_Type, File_Type, Library_Info_Type } from "../api/axiosNetworkRequest/library";
import LoadingOverlay from "./LoadingOverlay";

// ─── Types ────────────────────────────────────────────────────────────────────
interface LibraryViewProps {
  selectedCompanyId: number;
  showCheckbox?: boolean;
  selected?: Set<string>;
  onToggle?: (keys: string[], checked: boolean) => void;
  agentKey?: string;
  connectedForms?: Record<string, string>;
  onConnectForm?: (fileKey: string, formName: string) => void;
  onDisconnectForm?: (fileKey: string) => void;
  allKeys?: Set<string>;
  onKeysDiscovered?: (keys: string[]) => void;
}



// ─── File Row ─────────────────────────────────────────────────────────────────
const FileRow: React.FC<{
  file: File_Type;
  libName: string;
  deptName: string;
  showCheckbox?: boolean;
  selected?: Set<string>;
  onToggle?: (keys: string[], checked: boolean) => void;
  agentKey?: string;
  selectedCompanyId?: number;
  library_id?: number;
  connectedForm?: ConnectedForm_Type;
}> = ({
  file,
  libName,
  deptName,
  showCheckbox = false,
  selected = new Set(),
  onToggle = () => {},
  agentKey = "",
  selectedCompanyId = 0,
  library_id = 0,
  connectedForm,
}) => {
  const userInfo = useSelector((state: ReduxRootStateType) => state.user.user_info);

  const { data: formsData } = useGetFormsByCompany(selectedCompanyId);
  const uploadedForms = (formsData?.data ?? []).map(f => ({
    id: String(f.form_id),
    name: f.s3_path.split('/').pop() ?? f.s3_path,
  }));

  const isLinked = !!connectedForm;

  const disconnectMutation = useDisconnectForm(selectedCompanyId);
  const assignMutation     = useAssignForm();
  const unassignMutation   = useUnassignForm();
  const announceMutation   = useAnnounceContent();

  const fileKey = `${libName}||${deptName}||${file.file_name}`;
  const checked = selected.has(fileKey);

  const [hovered, setHovered]               = useState(false);
  const [anchor, setAnchor]                 = useState<null | HTMLElement>(null);
  const [announceOpen, setAnnounceOpen]     = useState(false);
  const [attachOpen, setAttachOpen]         = useState(false);
  const [assignOpen, setAssignOpen]         = useState(false);
  const [submissionsOpen, setSubmissionsOpen] = useState(false);
  const [disconnectConfirm, setDisconnectConfirm] = useState(false);
  const [attachSnack, setAttachSnack]     = useState(false);
  const [assignSnack, setAssignSnack]     = useState(false);
  const [announceSnack, setAnnounceSnack] = useState(false);

  const { data: assignmentsData, isFetching: assignmentsLoading } = useGetFormAssignments(assignOpen ? connectedForm?.connection_id : undefined);
  const existingAssignments = assignmentsData?.data ?? [];
  const preSelectedPeople   = [...new Set(existingAssignments.filter(a => !a.assignedViaRoleId).map(a => Number(a.user_id)))];
  const preSelectedRoles    = [...new Set(existingAssignments.map(a => a.assignedViaRoleId).filter((id): id is number => id != null).map(Number))];

  const isSignal = agentKey === "signal";
  const isSync   = agentKey === "sync";

  const { data: peopleData, isLoading: peopleLoading } = useGetUsersByCompany(selectedCompanyId);
  const currentUsers = peopleData?.data.find(c => c.company_id === selectedCompanyId)?.user_info ?? [];

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => showCheckbox && onToggle([fileKey], !checked)}
      sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        px: 2, py: 1.25, mx: 1, mb: 0.75,
        borderRadius: "10px",
        bgcolor: hovered ? "action.hover" : "background.default",
        border: "1px solid", borderColor: "divider",
        transition: "background-color 0.12s", cursor: "pointer",
      }}
    >
      {showCheckbox && (
        <Checkbox
          checked={checked}
          onClick={e => e.stopPropagation()}
          onChange={e => onToggle([fileKey], e.target.checked)}
          size="small"
          sx={{ p: 0, color: "text.disabled", "&.Mui-checked": { color: "primary.main" } }}
        />
      )}

      {/* File icon */}
      <Box sx={{
        width: 36, height: 36, borderRadius: "8px", flexShrink: 0,
        bgcolor: theme => theme.custom_colors.overlay,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <InsertDriveFileIcon sx={{ fontSize: "1.1rem", color: "text.secondary" }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: "text.primary", lineHeight: 1.4 }}>
          {file.file_name}
        </Typography>
      </Box>

      {!showCheckbox && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {isSync ? (
            <Button
              size="small"
              variant="contained"
              onClick={e => e.stopPropagation()}
              sx={{
                fontSize: "0.7rem", fontWeight: 700, px: 1.5, py: 0.4,
                borderRadius: "6px", letterSpacing: "0.06em",
                textTransform: "uppercase", minWidth: "unset",
              }}
            >
              Sync
            </Button>
          ) : isSignal ? (
            <Button
              size="small"
              variant="contained"
              startIcon={<CampaignOutlinedIcon sx={{ fontSize: "0.95rem !important" }} />}
              onClick={e => { e.stopPropagation(); setAnnounceOpen(true); }}
              sx={{
                fontSize: "0.7rem", fontWeight: 700, px: 1.5, py: 0.4,
                borderRadius: "6px", letterSpacing: "0.06em",
                textTransform: "uppercase", minWidth: "unset",
              }}
            >
              Announce Content
            </Button>
          ) : (
            <>
              {/* Per-file submissions icon: shows only when this file's connected
                  form has at least one submission (count rides along on the
                  company-wide connected-forms response — no per-file query). */}
              {isLinked && (connectedForm?.submission_count ?? 0) > 0 && (
                <Tooltip title="Form submissions available">
                  <IconButton
                    size="small"
                    onClick={e => { e.stopPropagation(); setSubmissionsOpen(true); }}
                    // Blue on light theme (low contrast as neutral), neutral on dark.
                    sx={{
                      color: theme => theme.palette.mode === "light" ? "primary.main" : "text.secondary",
                      "&:hover": { color: theme => theme.palette.mode === "light" ? "primary.dark" : "text.primary" },
                      p: "4px",
                    }}
                  >
                    <ArticleIcon sx={{ fontSize: "1.1rem" }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={isLinked ? `Connected: ${connectedForm?.s3_path.split('/').pop()}` : "No Form Connected"}>
                <IconButton
                  size="small"
                  onClick={e => { e.stopPropagation(); if (!isLinked) setAttachOpen(true); }}
                  sx={{ color: isLinked ? "success.main" : "text.disabled", "&:hover": { color: "primary.main" }, p: "4px" }}
                >
                  {isLinked ? <LinkIcon sx={{ fontSize: "1.1rem" }} /> : <LinkOffIcon sx={{ fontSize: "1.1rem" }} />}
                </IconButton>
              </Tooltip>
              <Tooltip title="More">
                <IconButton
                  size="small"
                  onClick={e => { e.stopPropagation(); setAnchor(e.currentTarget); }}
                  sx={{ color: "text.secondary", "&:hover": { color: "text.primary" }, p: "4px" }}
                >
                  <MoreVertIcon sx={{ fontSize: "1.1rem" }} />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                PaperProps={{ sx: { bgcolor: "background.paper", border: "1px solid", borderColor: "divider", boxShadow: "0 8px 24px rgba(0,0,0,0.5)", minWidth: 160 } }}
              >
                {isLinked ? (
                  [
                    <MenuItem key="remove" onClick={() => { setAnchor(null); setDisconnectConfirm(true); }} sx={{ color: "error.main", gap: 1.5 }}>
                      <LinkOffIcon sx={{ fontSize: "1.1rem" }} />
                      Remove Connected Form
                    </MenuItem>,
                    <MenuItem key="assign" onClick={() => { setAnchor(null); setAssignOpen(true); }} sx={{ gap: 1.5 }}>
                      <GroupAddIcon sx={{ fontSize: "1.1rem" }} />
                      Assign People
                    </MenuItem>,
                  ]
                ) : (
                  <MenuItem onClick={() => { setAnchor(null); setAttachOpen(true); }}>
                    Connect Form
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Box>
      )}

      <AttachFormDialog
        open={attachOpen}
        fileName={file.file_name}
        uploadedForms={uploadedForms}
        file_id={file.file_id}
        company_id={selectedCompanyId}
        library_id={library_id}
        created_by={userInfo?.user_id ?? 0}
        onClose={() => setAttachOpen(false)}
        onAttachSuccess={() => setAttachSnack(true)}
      />

      <FormSubmissionsDialog
        open={submissionsOpen}
        fileName={file.file_name}
        connectionId={connectedForm?.connection_id}
        onClose={() => setSubmissionsOpen(false)}
      />

      <AnnounceContentDialog
        open={announceOpen}
        fileName={file.file_name}
        selectedCompanyId={selectedCompanyId}
        onClose={() => setAnnounceOpen(false)}
        users={currentUsers}
        onSend={(people, roles) => {
          if (people.length > 0 || roles.length > 0) {
            announceMutation.mutate(
              { user_ids: people, role_ids: roles, file_name: file.file_name, dept_name: deptName },
              { onSuccess: () => setAnnounceSnack(true) }
            );
          }
        }}
      />

      <AnnounceContentDialog
        open={assignOpen}
        fileName={file.file_name}
        selectedCompanyId={selectedCompanyId}
        users={currentUsers}
        title="Assign People & Roles"
        buttonLabel="ASSIGN PEOPLE & ROLES"
        showWarning={false}
        preSelectedPeople={preSelectedPeople}
        preSelectedRoles={preSelectedRoles}
        isLoading={peopleLoading || assignmentsLoading}
        onClose={() => setAssignOpen(false)}
        onSend={async (people, roles) => {
          if (!connectedForm) return;

          const prevPeopleIds = new Set(preSelectedPeople);
          const addedPeople   = people.filter(id => !prevPeopleIds.has(id));
          const removedPeople = preSelectedPeople.filter(id => !people.includes(id));

          const prevRoleIds  = new Set(preSelectedRoles);
          const addedRoles   = roles.filter(id => !prevRoleIds.has(id));
          const removedRoles = preSelectedRoles.filter(id => !roles.includes(id));

          // Assign net-new individual people only (avoids duplicates)
          if (addedPeople.length > 0) {
            assignMutation.mutate(
              { connection_id: connectedForm.connection_id, user_ids: addedPeople, file_name: file.file_name, department_name: deptName },
              { onSuccess: () => setAssignSnack(true) }
            );
          }

          // Unassign deselected individual people
          for (const userId of removedPeople) {
            const assignment = existingAssignments.find(a => a.user_id === userId && !a.assignedViaRoleId);
            if (assignment) unassignMutation.mutate(assignment.assignment_id);
          }

          // Assign net-new roles (expand to user IDs)
          for (const roleId of addedRoles) {
            if (!roleId) continue;
            const res = await getUsersByRole(roleId);
            const userIds = res.data.map((u: { user_id: number }) => u.user_id);
            if (userIds.length > 0) {
              assignMutation.mutate(
                { connection_id: connectedForm.connection_id, user_ids: userIds, assignedViaRoleId: roleId, file_name: file.file_name, department_name: deptName },
                { onSuccess: () => setAssignSnack(true) }
              );
            }
          }

          // Unassign all assignments belonging to deselected roles
          for (const roleId of removedRoles) {
            const roleAssignments = existingAssignments.filter(a => a.assignedViaRoleId === roleId);
            for (const a of roleAssignments) unassignMutation.mutate(a.assignment_id);
          }
        }}
      />

      <Snackbar
        open={attachSnack}
        autoHideDuration={3000}
        onClose={() => setAttachSnack(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setAttachSnack(false)} severity="success" sx={{ width: "100%" }}>
          Form attached successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={assignSnack}
        autoHideDuration={3000}
        onClose={() => setAssignSnack(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setAssignSnack(false)} severity="success" sx={{ width: "100%" }}>
          Emails sent successfully!
        </Alert>
      </Snackbar>

      <LoadingOverlay isLoading={announceMutation.isPending} />

      {/* Disconnect Confirmation Dialog */}
      <Dialog
        open={disconnectConfirm}
        onClose={() => setDisconnectConfirm(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: "10px" } }}
      >
        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, pt: 2.5, pb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "text.primary" }}>
              Remove Connected Form
            </Typography>
            <IconButton size="small" onClick={() => setDisconnectConfirm(false)} sx={{ color: "text.secondary", "&:hover": { color: "text.primary" } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ px: 3, py: 3 }}>
            <Typography sx={{ color: "text.primary", fontSize: "0.9rem" }}>
              Are you sure you want to remove the connected form from this file?
            </Typography>
          </Box>
          <Box sx={{ px: 3, pb: 2.5, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <Button variant="outlined" onClick={() => setDisconnectConfirm(false)} sx={{ fontWeight: 600, borderRadius: "8px", fontSize: "0.8rem" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => { if (connectedForm) disconnectMutation.mutate(connectedForm.connection_id); setDisconnectConfirm(false); }}
              sx={{ fontWeight: 700, borderRadius: "8px", fontSize: "0.8rem" }}
            >
              Remove
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={announceSnack}
        autoHideDuration={3000}
        onClose={() => setAnnounceSnack(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setAnnounceSnack(false)} severity="success" sx={{ width: "100%" }}>
          Announcement sent successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ─── Department Row ───────────────────────────────────────────────────────────
const DeptRow: React.FC<{
  dept: Department_Type;
  libName: string;
  showCheckbox?: boolean;
  selected?: Set<string>;
  onToggle?: (keys: string[], checked: boolean) => void;
  agentKey?: string;
  selectedCompanyId?: number;
  library_id?: number;
  connectedMap?: Record<number, ConnectedForm_Type>;
  onKeysDiscovered?: (keys: string[]) => void;
  fileSearch?: string;
}> = ({
  dept,
  libName,
  showCheckbox = false,
  selected = new Set(),
  onToggle = () => {},
  agentKey = "",
  selectedCompanyId = 0,
  library_id = 0,
  connectedMap = {},
  onKeysDiscovered,
  fileSearch = '',
}) => {
  const [open, setOpen] = useState(false);

  const { data: fileData, isPending: filesLoading } = useGetFilesByDepartment(dept.department_id);
  const files = fileData?.data ?? [];
  const filteredFiles = fileSearch
    ? files.filter(f => f.file_name.toLowerCase().includes(fileSearch.toLowerCase()))
    : files;

  useEffect(() => {
    if (files.length > 0 && onKeysDiscovered) {
      onKeysDiscovered(files.map(f => `${libName}||${dept.department_name}||${f.file_name}`));
    }
  }, [files]);

  const deptKeys = files.map(f => `${libName}||${dept.department_name}||${f.file_name}`);

  const checkedCount  = deptKeys.filter(k => selected.has(k)).length;
  const allChecked    = checkedCount === deptKeys.length && deptKeys.length > 0;
  const indeterminate = checkedCount > 0 && !allChecked;

  // All hooks called above — safe to return early now
  if (fileSearch && !filesLoading && filteredFiles.length === 0) return null;
  const isOpen = fileSearch && filteredFiles.length > 0 ? true : open;

  return (
    <Box sx={{ mx: 1.5, mb: 1, borderRadius: "10px", border: "1px solid", borderColor: "divider", overflow: "hidden", bgcolor: "background.default" }}>
      <Box
        onClick={() => setOpen(p => !p)}
        sx={{
          display: "flex", alignItems: "center", gap: 1.5,
          px: 2, py: 1.25,
          cursor: "pointer", "&:hover": { bgcolor: "action.hover" }, transition: "background-color 0.15s",
        }}
      >
        {showCheckbox && (
          filesLoading ? (
            <CircularProgress size={16} thickness={5} sx={{ color: "text.disabled", flexShrink: 0 }} onClick={e => e.stopPropagation()} />
          ) : (
            <Checkbox
              checked={allChecked}
              indeterminate={indeterminate}
              onClick={e => e.stopPropagation()}
              onChange={e => onToggle(deptKeys, e.target.checked)}
              size="small"
              sx={{ p: 0, color: "text.disabled", "&.Mui-checked": { color: "primary.main" }, "&.MuiCheckbox-indeterminate": { color: "primary.main" } }}
            />
          )
        )}
        <Box sx={{
          width: 36, height: 36, borderRadius: "8px", flexShrink: 0,
          bgcolor: theme => theme.custom_colors.overlay,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <ApartmentIcon sx={{ fontSize: "1.1rem", color: "text.secondary" }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "text.primary" }}>
            {dept.department_name}
          </Typography>
          {!filesLoading && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {filteredFiles.length} File{filteredFiles.length !== 1 ? "s" : ""}
            </Typography>
          )}
        </Box>
        {agentKey === "sync" && (
          <Button
            size="small"
            variant="contained"
            onClick={e => e.stopPropagation()}
            sx={{
              fontSize: "0.7rem", fontWeight: 700, px: 1.5, py: 0.4,
              borderRadius: "6px", letterSpacing: "0.06em",
              textTransform: "uppercase", minWidth: "unset", flexShrink: 0,
            }}
          >
            Sync
          </Button>
        )}
        {isOpen && filesLoading
          ? <CircularProgress size={14} thickness={5} sx={{ color: "text.secondary", flexShrink: 0 }} />
          : isOpen
            ? <KeyboardArrowUpIcon   sx={{ color: "text.secondary", fontSize: "1.1rem", flexShrink: 0 }} />
            : <KeyboardArrowDownIcon sx={{ color: "text.secondary", fontSize: "1.1rem", flexShrink: 0 }} />
        }
      </Box>
      <Collapse in={isOpen} timeout={180}>
        <Box sx={{ pt: 0.5, pb: 0.25 }}>
          {filesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 2.5 }}>
              <CircularProgress size={18} thickness={5} sx={{ color: "primary.main" }} />
            </Box>
          ) : filteredFiles.length === 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 2 }}>
              No files found.
            </Typography>
          ) : filteredFiles.map(f => (
            <FileRow
              key={f.file_id}
              file={f}
              libName={libName}
              deptName={dept.department_name}
              showCheckbox={showCheckbox}
              selected={selected}
              onToggle={onToggle}
              agentKey={agentKey}
              selectedCompanyId={selectedCompanyId}
              library_id={library_id}
              connectedForm={connectedMap[f.file_id]}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── Library Row ──────────────────────────────────────────────────────────────
const LibraryRow: React.FC<{
  lib: Library_Info_Type;
  showCheckbox?: boolean;
  selected?: Set<string>;
  onToggle?: (keys: string[], checked: boolean) => void;
  agentKey?: string;
  selectedCompanyId?: number;
  allKeys?: Set<string>;
  connectedMap?: Record<number, ConnectedForm_Type>;
  onKeysDiscovered?: (keys: string[]) => void;
  fileSearch?: string;
}> = ({
  lib,
  showCheckbox = false,
  selected = new Set(),
  onToggle = () => {},
  agentKey = "",
  selectedCompanyId = 0,
  allKeys = new Set(),
  connectedMap = {},
  onKeysDiscovered,
  fileSearch = '',
}) => {
  const [open, setOpen]       = useState(false);
  const [hovered, setHovered] = useState(false);
  const theme                 = useTheme();
  const isOpen = fileSearch ? true : open;

  const { data: deptData, isPending: deptsLoading } = useGetDepartmentsByLibrary(lib.library_id);
  const departments = [...(deptData?.data ?? [])].sort((a, b) => a.department_name.localeCompare(b.department_name));

  // All hooks called — safe to return early now
  if (fileSearch && !deptsLoading && departments.length === 0) return null;
  const libKeys = [...allKeys].filter(k => k.startsWith(`${lib.library_name}||`));
  if (fileSearch && libKeys.length > 0 && !libKeys.some(k => k.split('||')[2]?.toLowerCase().includes(fileSearch.toLowerCase()))) {
    return null;
  }

  return (
    <Box sx={{ mb: 1.5, borderRadius: "12px", border: "1px solid", borderColor: "divider", overflow: "hidden", bgcolor: "background.paper" }}>
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => setOpen(p => !p)}
        sx={{
          display: "flex", alignItems: "center", gap: 1.5,
          px: 2, py: 1.5,
          backgroundColor: hovered ? "action.hover" : "background.paper",
          cursor: "pointer",
          transition: "background-color 0.15s ease",
        }}
      >
        {showCheckbox && (() => {
          const libAllKeys    = [...allKeys].filter(k => k.startsWith(`${lib.library_name}||`));
          const libSelKeys    = [...selected].filter(k => k.startsWith(`${lib.library_name}||`));
          const libAllChecked = libAllKeys.length > 0 && libSelKeys.length === libAllKeys.length;
          const libIndet      = libSelKeys.length > 0 && !libAllChecked;
          return deptsLoading ? (
            <CircularProgress size={16} thickness={5} sx={{ color: "primary.main", flexShrink: 0 }} onClick={e => e.stopPropagation()} />
          ) : (
            <Checkbox
              checked={libAllChecked}
              indeterminate={libIndet}
              onClick={e => e.stopPropagation()}
              onChange={e => onToggle(libAllKeys, e.target.checked)}
              size="small"
              sx={{ p: 0, color: "text.secondary", "&.Mui-checked": { color: "primary.main" }, "&.MuiCheckbox-indeterminate": { color: "primary.main" } }}
            />
          );
        })()}
        <Box sx={{
          width: 40, height: 40, borderRadius: "10px", flexShrink: 0,
          bgcolor: theme => theme.custom_colors.overlay,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <MenuBookIcon sx={{ fontSize: "1.2rem", color: "text.secondary" }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: theme.palette.text.primary, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            {lib.library_name}
          </Typography>
          {!deptsLoading && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {departments.length} Department{departments.length !== 1 ? "s" : ""}
            </Typography>
          )}
        </Box>
        {agentKey === "sync" && (
          <Button
            size="small"
            variant="contained"
            onClick={e => e.stopPropagation()}
            sx={{
              fontSize: "0.7rem", fontWeight: 700, px: 1.5, py: 0.4,
              borderRadius: "6px", letterSpacing: "0.06em",
              textTransform: "uppercase", minWidth: "unset", flexShrink: 0,
            }}
          >
            Sync
          </Button>
        )}
        {isOpen && deptsLoading
          ? <CircularProgress size={14} thickness={5} sx={{ color: "text.secondary", flexShrink: 0 }} />
          : isOpen
            ? <KeyboardArrowUpIcon   sx={{ color: "text.secondary", fontSize: "1.2rem", flexShrink: 0 }} />
            : <KeyboardArrowDownIcon sx={{ color: "text.secondary", fontSize: "1.2rem", flexShrink: 0 }} />
        }
      </Box>
      <Collapse in={isOpen} timeout={200}>
        <Box sx={{ pt: 0.5, pb: 0.75 }}>
          {deptsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 3 }}>
              <CircularProgress size={20} thickness={5} sx={{ color: "primary.main" }} />
            </Box>
          ) : departments.length > 0 ? (
            departments.map(dept => (
              <DeptRow
                key={dept.department_id}
                dept={dept}
                libName={lib.library_name}
                showCheckbox={showCheckbox}
                selected={selected}
                onToggle={onToggle}
                agentKey={agentKey}
                selectedCompanyId={selectedCompanyId}
                library_id={lib.library_id}
                connectedMap={connectedMap}
                onKeysDiscovered={onKeysDiscovered}
                fileSearch={fileSearch}
              />
            ))
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 3 }}>
              No departments found.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── Library View ─────────────────────────────────────────────────────────────
const LibraryView: React.FC<LibraryViewProps> = ({
  selectedCompanyId,
  showCheckbox = false,
  selected = new Set(),
  onToggle,
  agentKey = "",
  allKeys = new Set(),
  onKeysDiscovered,
}) => {
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [discoveredKeys, setDiscoveredKeys] = useState<Set<string>>(new Set());

  const handleKeysDiscovered = useCallback((keys: string[]) => {
    setDiscoveredKeys(prev => {
      const next = new Set(prev);
      keys.forEach(k => next.add(k));
      return next;
    });
  }, []);

  const { data, isPending: librariesLoading } = useGetLibrariesByCompany(selectedCompanyId);
  const currentLibraries = [...(data?.data.find(c => c.company_id === selectedCompanyId)?.library_info ?? [])].sort((a, b) => a.library_name.localeCompare(b.library_name));
  const { data: connectedData } = useGetConnectedFormsByCompany(selectedCompanyId);
  const connectedMap = Object.fromEntries(
    (connectedData?.data ?? []).map(c => [c.file_id, c])
  ) as Record<number, ConnectedForm_Type>;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <LoadingOverlay isLoading={librariesLoading} />
      {!showCheckbox && (
        <Box sx={{
          px: 3, py: 2,
          display: "flex", flexDirection: "column", gap: 1.5,
          flexShrink: 0,
          borderBottom: "1px solid", borderColor: "divider",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary", letterSpacing: "0.02em" }}>
              LIBRARIES
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
              Libraries: {currentLibraries.length}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, border: "1px solid", borderColor: "divider", borderRadius: "8px" }}>
            <SearchIcon sx={{ color: "text.secondary", fontSize: "1.1rem" }} />
            <InputBase
              placeholder="Search files..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ flex: 1, fontSize: "0.875rem", color: "text.primary" }}
            />
            {search && (
              <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.25, color: "text.secondary", "&:hover": { color: "text.primary" } }}>
                <CloseIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            )}
          </Box>
        </Box>
      )}
      <Box sx={{ height: 6, flexShrink: 0 }} />
      <Box sx={{ flex: 1, overflowY: "auto", px: 2, pt: 1, pb: 2 }}>
        {librariesLoading ? null : currentLibraries.length > 0 ? (
          <>
            {currentLibraries.map(lib => (
              <LibraryRow
                key={lib.library_id}
                lib={lib}
                showCheckbox={showCheckbox}
                selected={selected}
                onToggle={onToggle ?? (() => {})}
                agentKey={agentKey}
                selectedCompanyId={selectedCompanyId}
                allKeys={showCheckbox ? allKeys : discoveredKeys}
                connectedMap={connectedMap}
                onKeysDiscovered={showCheckbox ? onKeysDiscovered : handleKeysDiscovered}
                fileSearch={search}
              />
            ))}
            {search && discoveredKeys.size > 0 && ![...discoveredKeys].some(k => k.split('||')[2]?.toLowerCase().includes(search.toLowerCase())) && (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: "center", mt: 6 }}>
                No files found.
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: "center", mt: 6 }}>
            No libraries found.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default LibraryView;
